import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Instagram, AlertCircle, Info, MessageCircle, XCircle, SendIcon, RefreshCw, Facebook } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
// Using RTK Query hooks for all API calls instead of direct fetch
import { 
  useGetFacebookPagesMutation, 
  useSubscribeToPageMessagingMutation, 
  useUnsubscribeFromPageMessagingMutation,
  useSendInstagramMessageMutation
} from '@/common/reducers/backend-api/backend-api';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { InstagramMessageDialog } from './InstagramMessageDialog';
// Import Redux hooks and selectors
import { useWebDispatch, useWebSelector } from '@/website/store/web-store';
import { 
  fetchInstagramPages, // Use alias if you prefer
  selectFacebookPages,
  selectInstagramAccounts, // Import selectInstagramAccounts and alias it back or use the new name directly
  selectIsLoading,
  selectInstagramError,
  // Potentially other selectors like selectIsFacebookConnected if needed here
} from '@/common/reducers/InstagramSelectorSlice'; // Adjust path if needed

// Interface for the items displayed in the Instagram list
// Should match the structure of InstagramBusinessAccount from backend
interface InstagramAccountItem {
  id: string;
  name: string;
  username: string;
  profile_picture_url?: string;
  followers_count?: number;
  facebookPageId?: string;
  pageAccessToken?: string;
  messagingEnabled?: boolean;
}

// Interface for the items displayed in the Facebook list
interface FacebookPageItem {
  id: string;
  name: string;
  accessToken?: string; // Might not be needed directly in UI unless for debugging
  hasInstagramAccount: boolean;
  instagramAccountId?: string;
  // Add profile picture URL if available in backend response
  profile_picture_url?: string; 
}

export const InstagramPagesLoader: React.FC = () => {
  // Use Redux state instead of local state for pages, loading, error
  const dispatch = useWebDispatch();
  const facebookPages = useWebSelector(selectFacebookPages) as FacebookPageItem[];
  const instagramPages = useWebSelector(selectInstagramAccounts) as InstagramAccountItem[];
  const isLoading = useWebSelector(selectIsLoading);
  const error = useWebSelector(selectInstagramError);
  
  const [statusMessage, setStatusMessage] = useState<{type: 'success' | 'error' | 'info', message: string} | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<Record<string, { loading: boolean, success?: boolean, error?: string }>>({});
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedIgPage, setSelectedIgPage] = useState<InstagramAccountItem | null>(null); // State specific to IG message dialog
  
  // RTK Query Hooks
  const [getFacebookPages] = useGetFacebookPagesMutation(); // This mutation is used via the thunk now
  const [subscribeToPageMessaging] = useSubscribeToPageMessagingMutation();
  const [unsubscribeFromPageMessaging] = useUnsubscribeFromPageMessagingMutation();
  const [sendInstagramMessage] = useSendInstagramMessageMutation();
  
  // Fetch pages using the thunk on component mount
  useEffect(() => {
    dispatch(fetchInstagramPages());
  }, [dispatch]);
  
  // Update subscription status when instagramPages change
  useEffect(() => {
    const initialStatus: Record<string, { loading: boolean, success?: boolean, error?: string }> = {};
    instagramPages.forEach(page => {
      initialStatus[page.id] = {
        loading: false,
        success: page.messagingEnabled
      };
    });
    setSubscriptionStatus(prev => ({
      ...prev, // Keep existing statuses that might be loading
      ...initialStatus // Overwrite with new initial data
    }));
  }, [instagramPages]);
  
  // Refresh handler using the thunk
  const handleRefreshPages = () => {
    dispatch(fetchInstagramPages());
  };

  const handleSubscribeToMessaging = async (page: InstagramAccountItem) => {
    if (!page.facebookPageId || !page.pageAccessToken) {
      setSubscriptionStatus(prev => ({
        ...prev,
        [page.id]: {
          loading: false,
          success: false,
          error: 'Missing Facebook page ID or access token'
        }
      }));
      return;
    }

    // Set loading state
    setSubscriptionStatus(prev => ({
      ...prev,
      [page.id]: { loading: true }
    }));

    try {
      // Use RTK Query mutation hook instead of direct API call
      const response = await subscribeToPageMessaging({
        pageId: page.facebookPageId,
        pageAccessToken: page.pageAccessToken
      }).unwrap();

      // Handle null/undefined response
      if (!response) {
        console.error('Server returned null response for page messaging subscription');
        setSubscriptionStatus(prev => ({
          ...prev,
          [page.id]: {
            loading: false,
            success: false,
            error: 'Server returned empty response'
          }
        }));
        
        setStatusMessage({
          type: 'error',
          message: `Failed to subscribe to messaging for ${page.name}: Server returned empty response`
        });
        return;
      }

      // Update status based on the response
      setSubscriptionStatus(prev => ({
        ...prev,
        [page.id]: {
          loading: false,
          success: !!response.success,
          error: response.success ? undefined : (response.message || 'Unknown error')
        }
      }));

      // Show temporary status message
      if (response.success) {
        setStatusMessage({
          type: 'success',
          message: `Successfully subscribed to messaging for ${page.name}`
        });
      } else {
        setStatusMessage({
          type: 'error',
          message: `Failed to subscribe to messaging for ${page.name}: ${response.message || 'Unknown error'}`
        });
      }

      // Clear the status message after 5 seconds
      setTimeout(() => {
        setStatusMessage(null);
      }, 5000);

    } catch (err: any) {
      console.error('Error subscribing to page messaging:', err);
      
      // Set error status
      setSubscriptionStatus(prev => ({
        ...prev,
        [page.id]: {
          loading: false,
          success: false,
          error: err.message || 'Failed to subscribe to messaging'
        }
      }));

      // Show error message
      setStatusMessage({
        type: 'error',
        message: `Failed to subscribe to messaging: ${err.message || 'Unknown error'}`
      });
    }
  };

  const handleUnsubscribeFromMessaging = async (page: InstagramAccountItem) => {
    if (!page.facebookPageId || !page.pageAccessToken) {
      setSubscriptionStatus(prev => ({
        ...prev,
        [page.id]: {
          loading: false,
          success: false,
          error: 'Missing Facebook page ID or access token'
        }
      }));
      return;
    }

    // Set loading state
    setSubscriptionStatus(prev => ({
      ...prev,
      [page.id]: { loading: true }
    }));

    try {
      // Use RTK Query mutation hook
      const response = await unsubscribeFromPageMessaging({
        pageId: page.facebookPageId,
        pageAccessToken: page.pageAccessToken
      }).unwrap();

      // Handle null/undefined response
      if (!response) {
        console.error('Server returned null response for page messaging unsubscription');
        setSubscriptionStatus(prev => ({
          ...prev,
          [page.id]: {
            loading: false,
            success: false,
            error: 'Server returned empty response'
          }
        }));
        
        setStatusMessage({
          type: 'error',
          message: `Failed to disable messaging for ${page.name}: Server returned empty response`
        });
        return;
      }

      // Update status based on the response
      setSubscriptionStatus(prev => ({
        ...prev,
        [page.id]: {
          loading: false,
          success: false,
          error: response.success ? undefined : (response.message || 'Unknown error')
        }
      }));

      // Show temporary status message
      if (response.success) {
        setStatusMessage({
          type: 'success',
          message: `Successfully disabled messaging for ${page.name}`
        });
      } else {
        setStatusMessage({
          type: 'error',
          message: `Failed to disable messaging for ${page.name}: ${response.message || 'Unknown error'}`
        });
      }

      // Clear the status message after 5 seconds
      setTimeout(() => {
        setStatusMessage(null);
      }, 5000);

    } catch (err: any) {
      console.error('Error unsubscribing from page messaging:', err);
      
      // Set error status
      setSubscriptionStatus(prev => ({
        ...prev,
        [page.id]: {
          loading: false,
          success: true, // Keep it as true since we failed to unsubscribe
          error: err.message || 'Failed to disable messaging'
        }
      }));

      // Show error message
      setStatusMessage({
        type: 'error',
        message: `Failed to disable messaging: ${err.message || 'Unknown error'}`
      });
    }
  };

  // Handler for opening the message dialog (specific to Instagram accounts)
  const handleOpenMessageDialog = (page: InstagramAccountItem) => {
    setSelectedIgPage(page);
    setMessageDialogOpen(true);
  };

  // Handler for sending a message (specific to Instagram accounts)
  const handleSendMessage = async (recipientId: string, text: string) => {
    if (!selectedIgPage) {
      return { success: false, error: 'No Instagram page selected for sending message' };
    }
    
    // Use facebookPageId from the selected IG account to call the API
    if (!selectedIgPage.facebookPageId) {
        return { success: false, error: 'Missing linked Facebook Page ID for this Instagram account.' };
    }

    try {
      console.log('Sending Instagram message with params:', {
        pageId: selectedIgPage.facebookPageId, // Use Facebook Page ID
        recipientId,
        text
      });
      
      const response = await sendInstagramMessage({
        pageId: selectedIgPage.facebookPageId, // Use Facebook Page ID
        recipientId,
        text
      });
      
      console.log('Instagram message API response:', response);
      
      // Check if the response was successful (RTK Query puts successful responses in the 'data' property)
      if ('data' in response) {
        const result = response.data;
        
        if (result.success) {
          return {
            success: true,
            messageId: result.messageId
          };
        } else if (result.error) {
          return {
            success: false,
            error: result.error
          };
        }
      }
      // Handle error responses (RTK Query puts errors in the 'error' property)
      else if ('error' in response) {
        const errorData = response.error;
        console.error('Error from API:', errorData);
        
        // Try to extract a meaningful error message
        let errorMessage = 'Unknown error occurred';
        
        if (typeof errorData === 'object' && errorData !== null) {
          // FetchBaseQueryError has a data property that might contain the error details
          if ('data' in errorData && errorData.data) {
            errorMessage = JSON.stringify(errorData.data);
          } 
          // SerializedError has a message property
          else if ('message' in errorData && errorData.message) {
            errorMessage = errorData.message;
          }
        }
        
        return {
          success: false,
          error: errorMessage
        };
      }
      
      // Fallback for unexpected response format
      return { 
        success: false, 
        error: 'Invalid response from server' 
      };
    } catch (err: any) {
      console.error('Error sending Instagram message:', err);
      
      let errorMessage = 'Failed to send message';
      
      if (err.data) {
        console.error('API Response error data:', err.data);
        errorMessage = JSON.stringify(err.data);
      }
      
      if (err.status) {
        console.error('API Response status:', err.status);
      }
      
      return { 
        success: false, 
        error: err.data?.error || err.message || errorMessage
      };
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-md font-semibold">Connected Accounts</h3>
        <Button 
          onClick={handleRefreshPages} 
          disabled={isLoading}
          size="sm"
        >
          {isLoading ? (
            <> <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading... </>
          ) : (
            <> <RefreshCw className="mr-2 h-4 w-4" /> Refresh Accounts </>
          )}
        </Button>
      </div>
      
      {statusMessage && (
        <Alert className={`${
          statusMessage.type === 'error' 
            ? 'bg-red-50 border-red-200 text-red-700' 
            : statusMessage.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-blue-50 border-blue-200 text-blue-700'
        }`}>
          {statusMessage.type === 'error' && <AlertCircle className="h-4 w-4 mr-2" />}
          {statusMessage.type === 'info' && <Info className="h-4 w-4 mr-2" />}
          <AlertDescription>
            {statusMessage.message}
          </AlertDescription>
        </Alert>
      )}
      
      {error && !isLoading && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Accounts</AlertTitle>
          <AlertDescription>
             {error}. Please ensure your Facebook account is connected and try refreshing.
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="flex justify-center items-center p-4">
          <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          <span className="ml-2 text-gray-500">Loading connected accounts...</span>
        </div>
      )}

      {!isLoading && !error && (
        <>
          {facebookPages.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-600">Facebook Pages</h4>
              {facebookPages.map(page => (
                <Card key={page.id} className="overflow-hidden">
                  <CardContent className="p-3 flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Facebook className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium">{page.name}</h5>
                       <p className="text-xs text-gray-500">ID: {page.id}</p>
                    </div>
                     <Badge variant={page.hasInstagramAccount ? "secondary" : "outline"} className="text-xs">
                      {page.hasInstagramAccount ? `Linked to IG: ${page.instagramAccountId}` : "No IG Account Linked"}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {instagramPages.length > 0 && (
             <div className="space-y-3">
               <h4 className="text-sm font-medium text-gray-600">Instagram Business Accounts</h4>
              {instagramPages.map(page => (
                <Card key={page.id} className="overflow-hidden">
                  <CardContent className="p-3 flex items-center space-x-3">
                    {page.profile_picture_url ? (
                      <img src={page.profile_picture_url} alt={page.name} className="w-12 h-12 rounded-full object-cover"/>
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-200 via-pink-200 to-orange-200 rounded-full flex items-center justify-center">
                        <Instagram className="h-6 w-6 text-purple-700" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h5 className="font-medium">{page.name}</h5>
                      <p className="text-sm text-gray-500">@{page.username}</p>
                      {page.facebookPageId && <p className="text-xs text-gray-400">Linked FB Page: {page.facebookPageId}</p>}
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      {page.followers_count !== undefined && (
                        <Badge variant="outline" className="text-xs">
                          {page.followers_count.toLocaleString()} followers
                        </Badge>
                      )}
                      <div className="flex items-center space-x-2">
                        {subscriptionStatus[page.id]?.loading ? (
                          <Button disabled variant="outline" size="sm">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...
                          </Button>
                        ) : subscriptionStatus[page.id]?.success ? (
                          <TooltipProvider> <Tooltip> <TooltipTrigger asChild>
                            <Button variant="outline" size="sm" className="text-green-600 bg-green-50 hover:bg-green-100 border-green-200" onClick={() => handleUnsubscribeFromMessaging(page)}>
                              <MessageCircle className="h-4 w-4 mr-2" /> Enabled
                            </Button>
                          </TooltipTrigger> <TooltipContent><p>Messaging Enabled. Click to disable.</p></TooltipContent> </Tooltip> </TooltipProvider>
                        ) : (
                          <TooltipProvider> <Tooltip> <TooltipTrigger asChild>
                            <Button variant="outline" size="sm" onClick={() => handleSubscribeToMessaging(page)}>
                              <XCircle className="h-4 w-4 mr-2" /> Enable Messaging
                            </Button>
                          </TooltipTrigger> <TooltipContent><p>Enable messaging to receive and send messages via this account.</p></TooltipContent> </Tooltip> </TooltipProvider>
                        )}
                        <TooltipProvider> <Tooltip> <TooltipTrigger asChild>
                           <Button variant="outline" size="sm" onClick={() => handleOpenMessageDialog(page)} disabled={!subscriptionStatus[page.id]?.success || subscriptionStatus[page.id]?.loading}>
                             <SendIcon className="h-4 w-4" />
                           </Button>
                         </TooltipTrigger> <TooltipContent><p>Send a test message</p></TooltipContent> </Tooltip> </TooltipProvider>
                      </div>
                      {subscriptionStatus[page.id]?.error && (
                        <p className="text-xs text-red-500 mt-1 text-right"> Error: {subscriptionStatus[page.id].error} </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {facebookPages.length === 0 && instagramPages.length === 0 && !isLoading && !error && (
             <Alert className="bg-blue-50 border-blue-200 text-blue-700">
               <Info className="h-4 w-4 mr-2" />
               <AlertDescription>
                 No Facebook pages or linked Instagram Business Accounts found. Ensure your Facebook account is connected and has granted the necessary permissions.
               </AlertDescription>
             </Alert>
          )}
        </>
      )}

      {selectedIgPage && (
        <InstagramMessageDialog
          open={messageDialogOpen}
          onClose={() => setMessageDialogOpen(false)}
          pageId={selectedIgPage.facebookPageId || ''}
          pageName={selectedIgPage.name}
          onSendMessage={handleSendMessage}
        />
      )}
    </div>
  );
}; 